/**
 * Client-side aura display name + tooltip description loader (#336) —
 * AUTO-DERIVED from content, mirroring `catalog.ts`'s (#239) build-time
 * `import.meta.glob` pattern exactly (the same idiom `abilityPool.ts` (#323)
 * reuses for champion ability pools).
 *
 * WHY GLOB, NOT A HAND-TABLE: mirrors `abilityLabels.ts`/`abilityFacts.ts`
 * (#274/#412/#461's ability-side auto-derive), applied here to auras — aura
 * defs are ALREADY standalone one-per-file JSON
 * (`packages/content/data/auras/*.json`), each
 * carrying its own `id`/`slug`/`name`/`description` — so every shipped
 * aura's real name+description can be read directly off disk at build time
 * with zero hand-copying and zero drift risk. #336's Part B explicitly
 * prefers this pattern over adding `name`/`description` to `WireAura` (the
 * wire stays exactly as lean as it is today) and over a hand-table (which
 * would just re-introduce the placeholder-drift problem #274 exists to
 * retire for abilities).
 *
 * SOURCE: name+description are read at BUILD TIME from the on-disk aura JSON
 * via `import.meta.glob`, narrow-schema-STRIPPED down to `{id, slug, name,
 * description}` — never `durationTicks`/`stacking`/`primitives`/etc (those
 * ARE balance-sensitive and must never reach the client bundle; same
 * ContentBundle-avoidance rationale as `catalog.ts` — we deliberately do NOT
 * import `@pit/content`'s runtime package into the browser, which would pull
 * in `node:crypto` + the loader).
 *
 * `name`/`description` are `.optional()` on `AURA_DEF_SCHEMA` (see that
 * schema's doc comment) — every SHIPPED aura authors real, non-empty text,
 * but an unknown/undescribed def id (or a hand-built sim-test-only aura that
 * never ships as content) resolves to `null`, and callers fall back to a
 * name-only/no-tooltip presentation (mirrors `abilityDescription`'s
 * null-fallback convention).
 *
 * #502: `durationTicks` now ALSO reaches the client (the ONE balance-relevant
 * number let through, deliberately) — a player-facing "how long does this
 * buff/debuff last" is not privileged information; it's visible the instant
 * the aura is applied in a real match (the countdown timer `AuraChip`/
 * `AuraTooltip` already render from the WIRE `expiresAtTick`, live). This is
 * the STATIC authored counterpart of that same number, needed anywhere a
 * duration must be shown BEFORE the aura exists as a live instance (e.g. an
 * ability tooltip's "grants a buff lasting Xs", pre-cast — see
 * `abilityFacts.ts`'s `appliedAuraDefIds` + `AbilityTooltip.tsx`). Still no
 * `polarity`/`primitives`/`stacking`/etc — those remain stripped.
 */

import { z } from "zod";
import { AURA_DEF_ID_SCHEMA, AURA_DURATION_SCHEMA, SLUG_SCHEMA } from "@pit/protocol";

/**
 * Minimal aura metadata schema — only the tooltip-relevant fields. `z.object`
 * (not `strictObject`) strips `polarity`/`primitives`/`stacking`/etc, so
 * those never reach the client.
 */
const AURA_META_SCHEMA = z.object({
  id: AURA_DEF_ID_SCHEMA,
  slug: SLUG_SCHEMA,
  name: z.string().min(1).max(64).optional(),
  description: z.string().max(280).optional(),
  durationTicks: AURA_DURATION_SCHEMA,
});

export interface AuraLabel {
  readonly name: string | null;
  readonly description: string | null;
  /** #502: authored duration — a tick count, `"permanent"`, or
   *  `"scaledOnly"` (no static number to show; the real duration only
   *  exists at cast time, see `AURA_DURATION_SCHEMA`). */
  readonly durationTicks: number | "permanent" | "scaledOnly";
}

const NO_LABEL: AuraLabel = { name: null, description: null, durationTicks: "permanent" };

/**
 * Build-time map of every on-disk aura JSON -> its `{name, description,
 * durationTicks}`. Same literal-`import.meta.glob` rule as
 * `catalog.ts`/`abilityPool.ts`: the call MUST be the literal form for
 * Vite's static transform. Under vitest the same committed JSON resolves
 * (Vite's transform pipeline applies to test files too). A malformed aura
 * JSON throws here (loud) — but content is already validated by
 * `@pit/content`'s own tests + the server loader, so this can only fail if
 * that upstream gate was skipped.
 */
function loadAuraLabels(): ReadonlyMap<string, AuraLabel> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/auras/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Map<string, AuraLabel>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const meta = AURA_META_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.set(meta.id, {
      name: meta.name ?? null,
      description: meta.description ?? null,
      durationTicks: meta.durationTicks,
    });
  }
  return out;
}

const AURA_LABELS_BY_ID: ReadonlyMap<string, AuraLabel> = loadAuraLabels();

/** Display name for an aura def id, or `null` for an unknown/undescribed def. */
export function auraLabel(defId: string): string | null {
  return (AURA_LABELS_BY_ID.get(defId) ?? NO_LABEL).name;
}

/** Tooltip description for an aura def id, or `null` when none is authored. */
export function auraDescription(defId: string): string | null {
  return (AURA_LABELS_BY_ID.get(defId) ?? NO_LABEL).description;
}

/**
 * #502: authored duration for an aura def id, or `null` for an unknown def
 * (distinct from `"permanent"` — an unknown def has no authored duration at
 * all, whereas a real permanent aura explicitly has one). Callers wanting a
 * player-facing string should feed this through `formatAuraDuration` below.
 */
export function auraDurationTicks(defId: string): number | "permanent" | "scaledOnly" | null {
  return AURA_LABELS_BY_ID.get(defId)?.durationTicks ?? null;
}

/**
 * #502: format an authored `durationTicks` value into player-facing text —
 * whole seconds, ceil-rounded (mirrors `ticksToSeconds`'s convention in
 * `ui/hud-util.ts`, applied to a STATIC authored duration instead of a live
 * countdown). `"permanent"` -> `"Permanent"`; `"scaledOnly"` -> `null` (no
 * single static number exists — the real duration is only known at cast
 * time, e.g. Kidney Shot's `durationPerPointTable`); `null` (unknown def)
 * -> `null`. `tickRate` mirrors every other client call site's hardcoded
 * `const TICK_RATE = 30` convention (see `ActionBar.tsx`/`AuraChip.tsx` and
 * peers) rather than being threaded through from content at build time.
 */
export function formatAuraDuration(
  durationTicks: number | "permanent" | "scaledOnly" | null,
  tickRate: number,
): string | null {
  if (durationTicks === null || durationTicks === "scaledOnly") return null;
  if (durationTicks === "permanent") return "Permanent";
  return `${String(Math.ceil(durationTicks / tickRate))}s`;
}
